import { assessmentOpensAt, isWithinAssessmentWindow, hasAssessmentForAppt } from "./symptomAssessment";

/**
 * What the "Take Symptom Assessment" button on the symptom-score card offers.
 *
 * It used to look only at appointments for the condition picked in "Viewing care
 * for", which lists conditions with a care plan. A patient whose upcoming visit
 * was for another condition, or for a new one, saw the button greyed out with
 * "Assessments can be taken within 1 week of your next scheduled appointment",
 * even when that visit was two days away. It now offers any appointment that can
 * be assessed, preferring the condition on screen, and says which visit it is for.
 *
 * Whether an appointment can be assessed is still symptomAssessment.js's rule,
 * mirrored from the server's QuestionnaireRules; this only chooses between them.
 */

// The "patient doesn't know their diagnosis" record has been relabelled more than
// once — originally "I don't know my diagnosis yet!", then "Not Listed", now
// "Unsure of my diagnosis". Appointments carry the name they were booked under, so
// historical rows still hold the older labels. These visits have no symptom
// questionnaire; the patient describes their symptoms instead.
const UNSURE_DIAGNOSIS_NAMES = [
  "unsure of my diagnosis",
  "not listed",
  "i don't know my diagnosis yet!",
];

export const isUnsureDiagnosis = (diagName) =>
  UNSURE_DIAGNOSIS_NAMES.includes(String(diagName ?? "").trim().toLowerCase());

const byStart = (a, b) => new Date(a.apptStartTime) - new Date(b.apptStartTime);

/**
 * @param appts          the patient's upcoming, not-cancelled appointments
 * @param selectedDiagId the condition shown on the card, if any
 * @returns one of
 *   { kind: "open", appt }              — can be taken now, for appt
 *   { kind: "done", appt }              — appt's assessment is already in, nothing else is open
 *   { kind: "upcoming", appt, opensAt } — nothing open yet; the next one opens at opensAt
 *   { kind: "none" }                    — no upcoming appointment has an assessment
 */
export const assessmentButtonState = (appts = [], selectedDiagId, now = new Date()) => {
  const all = appts || [];
  const assessable = all
    .filter((a) => a && a.status !== "CANCELED" && !isUnsureDiagnosis(a.diagName))
    .sort(byStart);
  const selectedFirst = (list) => {
    const mine = list.filter((a) => Number(a.diagId) === Number(selectedDiagId));
    return [...mine, ...list.filter((a) => !mine.includes(a))];
  };

  const inWindow = assessable.filter((a) => isWithinAssessmentWindow(a, all, now));

  const open = selectedFirst(inWindow).find((a) => !hasAssessmentForAppt(a));
  if (open) return { kind: "open", appt: open };

  const done = selectedFirst(inWindow).find(hasAssessmentForAppt);
  if (done) return { kind: "done", appt: done };

  // The soonest to open, whatever the condition: this is "when can I next take one".
  const later = assessable
    .filter((a) => new Date(a.apptStartTime) > now && !hasAssessmentForAppt(a))
    .map((a) => ({ a, opens: assessmentOpensAt(a, all) }))
    .sort((x, y) => x.opens - y.opens)[0]?.a;
  if (later) return { kind: "upcoming", appt: later, opensAt: assessmentOpensAt(later, all) };

  return { kind: "none" };
};
